// Copyright (c) Ping. All rights reserved.
//
// Populates the self-profile store once the session is authenticated, so the
// sidebar account chip + chat self-avatar render the real avatar/name on first
// paint (not just after the Settings modal is opened). Mounted once in the
// authenticated shell.

import { useEffect } from 'react';

import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import { registerCurrentUserDisplayName } from '@ping/ui/src/sync/registerCurrentUser';

import { log } from '@core/log/log';
import { retrieveIdentity, storeIdentity } from '@core/persistence/identity';
import { fetchMediaDownloadUrl } from '@features/chat/mediaUpload';
import { getProfile } from '@features/settings/web/profilesApi';

import { useSelfProfile } from './selfProfileStore';

export function useSelfProfileBootstrap(): void {
  const authed = useSessionStore((s) => s.status.kind === 'authenticated');
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    void (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        let avatarUrl: string | null = null;
        if (profile.avatarMediaId !== null) {
          avatarUrl = await fetchMediaDownloadUrl(profile.avatarMediaId).catch(() => null);
        }
        if (cancelled) return;
        useSelfProfile.getState().setProfile({
          displayName: profile.displayName,
          avatarMediaId: profile.avatarMediaId,
          avatarUrl,
        });
        // Persist the canonical display name into the LOCAL identity. A freshly
        // LINKED device stores `displayName: ''` at link-verify and relies on
        // exactly this step ("bootstrap will fetch it from GET /v1/me/profile
        // and storeIdentity it again" — LinkDevicePairingHost). Without it the
        // Settings → Account name and the sidebar chip stay blank on a linked
        // device. Also re-register it so the chat self-name resolves.
        if (profile.displayName) {
          registerCurrentUserDisplayName(profile.displayName);
          const persisted = await retrieveIdentity();
          if (cancelled) return;
          if (persisted && persisted.displayName !== profile.displayName) {
            await storeIdentity({ ...persisted, displayName: profile.displayName });
          }
        }
      } catch (err) {
        log.debug({ event: 'selfProfile.bootstrap.failed', err: String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authed]);
}
